import { useCallback, useEffect, useMemo, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { arrayMove } from "@dnd-kit/sortable"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { useAppDispatch } from "hooks/redux"
import { useAccountId } from "resources/account"
import { useReadPreference, useSavePreference, getPreferenceQueryKey } from "hooks/preferences"
import { ERROR, LOADING } from "helpers/data-statuses"
import { orderInstances } from "../store/instances-slice"

// Account-scoped singleton, same key/type shape as the theme-mode preference.
export const TAB_ORDER_PREFERENCE_KEY = "dashboardTabOrder"
export const TAB_ORDER_PREFERENCE_TYPE = "ui"

const EMPTY_INSTANCES: readonly never[] = []

// The read cache holds the rows the server actually returned, so a user who has never reordered
// has an empty list here rather than a missing entry. Empty is a real rollback target: dropping it
// leaves setQueryData with undefined, which TanStack treats as a no-op.
type PersistedOrderCache = [{ value: string[] }] | []

function isOrderIdList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((id) => typeof id === "string")
}

function isPersistedOrderCache(value: unknown): value is PersistedOrderCache {
  if (!Array.isArray(value)) return false
  if (value.length === 0) return true
  return value.length === 1 && isOrderIdList((value[0] as { value?: unknown })?.value)
}

interface OrderableInstance {
  instanceId: string
  lastOpenedAt: string | null
}

export interface DashboardTabOrder<T> {
  orderedInstances: T[]
  // True only until the stored order is known for the first time. Drawing tabs before then shows
  // recency order and then jumps, which is the reshuffling this feature exists to stop.
  isOrderLoading: boolean
  // Moves activeId to overId's position, as dnd-kit reports a drop.
  reorder: (activeId: string, overId: string) => void
}

/**
 * The switcher's tab order, read from an account-scoped preference and written on each drop.
 *
 * A drop lands in the preference read cache immediately, which is what carries it through the
 * remount a dashboard switch forces on the header. Saves are scoped so React Query runs them one at
 * a time in call order, at cache level rather than in this component, so repeated drags cannot race
 * and a queued write is not lost if the strip unmounts first. Serialized writes also make the save
 * hook's own cache seeding safe: the last write to settle is the newest one.
 *
 * A failed save rolls back synchronously to the last order the server actually confirmed, tracked
 * separately from the read cache since that cache can hold an unpersisted optimistic write. If a
 * later drag was already queued behind the failure, its own onConfirmed seeds the cache again, so
 * the rollback is self-correcting rather than final.
 */
export function useDashboardTabOrder<T extends OrderableInstance>(
  instances: readonly T[] | null | undefined,
  enabled: boolean,
): DashboardTabOrder<T> {
  const accountId = useAccountId()
  const [savedOrder, orderStatus] = useReadPreference(
    TAB_ORDER_PREFERENCE_KEY,
    TAB_ORDER_PREFERENCE_TYPE,
    null,
    { enabled },
  ) as [string[] | null | undefined, string]
  // Shared with the onError guard below, so it can check for other saves still queued in this
  // same scope rather than duplicating the string and risking the two drifting apart.
  const saveScopeId = `preference-save:${accountId}:${TAB_ORDER_PREFERENCE_KEY}`
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  const readQueryKey = useMemo(
    () => [getPreferenceQueryKey(accountId, TAB_ORDER_PREFERENCE_KEY, TAB_ORDER_PREFERENCE_TYPE)],
    [accountId],
  )

  // Last server-confirmed order, refreshed on each real read settle (LOADING covers a remount's
  // background refetch too — a remount's read cache can still hold a prior mount's unconfirmed
  // optimistic write) and on every save's onConfirmed below.
  const lastPersistedOrderRef = useRef<PersistedOrderCache | undefined>(undefined)
  useEffect(() => {
    if (orderStatus === LOADING) return
    const cached = queryClient.getQueryData(readQueryKey)
    lastPersistedOrderRef.current = isPersistedOrderCache(cached) ? cached : undefined
  }, [orderStatus, queryClient, readQueryKey])

  // onConfirmed is a mutation-level callback (registered once, at the hook call below), unlike the
  // onError passed to the returned mutate() function per drag. That distinction matters here: two
  // drags queued back to back share one mutation observer, and starting the second one detaches the
  // observer from the first before it settles — silently dropping any per-call callback on that
  // first call, success or failure. onConfirmed still fires for a drag superseded this way, so this
  // ref cannot go stale to a save that actually landed while a later one was already in flight.
  const savePreference = useSavePreference(TAB_ORDER_PREFERENCE_KEY, TAB_ORDER_PREFERENCE_TYPE, {
    scopeId: saveScopeId,
    onConfirmed: (savedValue: unknown) => {
      lastPersistedOrderRef.current = isOrderIdList(savedValue) ? [{ value: savedValue }] : undefined
    },
  })

  const persistedOrder = Array.isArray(savedOrder) ? savedOrder : null

  const orderedInstances = useMemo(
    () => orderInstances(instances ?? EMPTY_INSTANCES, persistedOrder),
    [instances, persistedOrder],
  )

  // `undefined` means the read has never resolved; a resolved read with no saved row gives null.
  // Reading the status instead would also catch background refetches, since convertTanStackStatus
  // maps isFetching to LOADING, and the strip would skeleton on every dashboard switch. A read that
  // cannot run at all reports pending too, so an absent accountId must not read as still loading.
  const isOrderLoading = enabled
    && Boolean(accountId)
    && savedOrder === undefined
    && orderStatus !== ERROR

  const reorder = useCallback(
    (activeId: string, overId: string) => {
      if (activeId === overId) return

      const currentIds = orderedInstances.map((instance) => instance.instanceId)
      const from = currentIds.indexOf(activeId)
      const to = currentIds.indexOf(overId)
      if (from === -1 || to === -1) return

      // The full strip is written, not just the ids the saved order already knew, so every
      // instance gets a fixed position instead of staying on the recency fallback.
      const nextIds = arrayMove(currentIds, from, to)
      queryClient.setQueryData(readQueryKey, [{ value: nextIds }])
      savePreference(nextIds, {
        onError: () => {
          dispatch(NotificationCreators.add("error", null, "Could not save your tab order."))
          // The scope serializer dequeues the next queued save the moment this one fails, so
          // rolling back here can race that save's own cache-seed and revert past what it already
          // landed — two visible jumps for what read as one failure. Only roll back once nothing
          // else in this save's scope is still in flight.
          //
          // >0, not >1: unlike preferences.js's mutation-level onSuccess, this per-call onError is
          // delivered by MutationObserver's notify, which runs after the "error" dispatch already
          // flipped this mutation's status, so isMutating never counts itself.
          const isScopeDraining = queryClient.isMutating({
            predicate: (mutation) => mutation.options.scope?.id === saveScopeId,
          }) > 0
          if (!isScopeDraining) {
            // The confirmed order, not the cache value from right before this drag — an earlier
            // drag in the same burst can have skipped its own rollback under this same guard.
            queryClient.setQueryData(readQueryKey, lastPersistedOrderRef.current)
          }
        },
      })
    },
    [orderedInstances, savePreference, queryClient, readQueryKey, saveScopeId, dispatch],
  )

  return { orderedInstances, isOrderLoading, reorder }
}
